import React, { useCallback, useMemo } from "react";
import styled from "@emotion/styled";
import { Box } from "@mui/material";
import { useInstrumentUrl, useSecurityImages } from "@hooks";
import { SecurityImage } from "@components/SecurityImage";
import { ClassificationChartLegend } from "./legend";
import { ClassificationDonutChart } from "./ClassificationDonutChart";
import { useClassificationDrillDown } from "./useClassificationDrillDown";
import { ClassificationBreakdownData, ClassificationChartProps } from "./types";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  width: 100%;

  ${({ theme }) => theme.breakpoints.up("md")} {
    display: grid;
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    column-gap: ${({ theme }) => theme.spacing(17)};
    align-items: start;
  }
`;

export const ClassificationChart = ({
  breakdowns,
  isFetching = false,
  legendSectionTitle,
  legendTitleTooltipKey,
  renderLegendBeforeEl,
  actions,
}: ClassificationChartProps) => {
  const {
    donutData,
    hoveredItem,
    currentTotalValue,
    mainBreakdown,
    rootTotalValue,
    breadcrumbs,
    legendChartTitle,
    legendItems,
    handleSliceClick,
    handleLegendClick,
    handleHover,
  } = useClassificationDrillDown(breakdowns, legendSectionTitle);

  const getInstrumentUrl = useInstrumentUrl();

  const getBnUrl = useCallback(
    (item: ClassificationBreakdownData) =>
      getInstrumentUrl({
        type: item.investmentType,
        isin: item.isin,
        quoteProvider: item.quoteProvider,
      }),
    [getInstrumentUrl]
  );

  const isClickable = useCallback(
    (item: ClassificationBreakdownData) =>
      Boolean(item.hasChildren) || Boolean(getBnUrl(item)),
    [getBnUrl]
  );

  const leafSecurities = useMemo(
    () =>
      breakdowns
        .filter(
          (b) =>
            !b.hasChildren &&
            (b.isin ? !b.isin.startsWith("_NA_") : Boolean(b.tickerSymbol))
        )
        .map((b) => ({
          isin: b.isin,
          tickerSymbol: b.tickerSymbol,
          quoteProvider: b.quoteProvider,
          hasValidIsin: Boolean(b.isin),
          type: b.investmentType,
        })),
    [breakdowns]
  );
  const images = useSecurityImages(leafSecurities);

  const combinedBeforeEl = useCallback(
    (item: ClassificationBreakdownData) => {
      const custom = renderLegendBeforeEl?.(item);

      if (item.isin || item.tickerSymbol) {
        const src = images[item.isin || ""] || images[item.tickerSymbol || ""];
        return (
          <Box display="flex" alignItems="center" gap={2}>
            <SecurityImage src={src} width={24} height={24} />
            {custom}
          </Box>
        );
      }

      return custom;
    },
    [renderLegendBeforeEl, images]
  );

  const handleDonutSliceClick = useCallback(
    (item: ClassificationBreakdownData) => {
      if (item.hasChildren) {
        handleSliceClick(item);
        return;
      }
      const url = getBnUrl(item);
      if (url) window.location.href = url;
    },
    [handleSliceClick, getBnUrl]
  );

  if (!breakdowns.length && !isFetching) {
    return null;
  }

  return (
    <Container>
      <Box sx={{ width: "100%" }}>
        <ClassificationDonutChart
          data={donutData}
          ringTotalValue={hoveredItem?.value ?? currentTotalValue}
          centerSubtitle={hoveredItem?.name ?? mainBreakdown?.name ?? ""}
          isLoading={isFetching}
          hoveredItem={hoveredItem}
          onSectionClick={handleDonutSliceClick}
          onSectionHover={handleHover}
          isItemClickable={isClickable}
        />
      </Box>

      <Box>
        {actions}
        <ClassificationChartLegend
          breadcrumbs={breadcrumbs}
          chartTitle={legendChartTitle}
          chartTitleTooltipKey={legendTitleTooltipKey}
          items={legendItems}
          rootTotalValue={rootTotalValue}
          isLoading={isFetching}
          hoveredItem={hoveredItem}
          renderBeforeEl={combinedBeforeEl}
          onItemHover={handleHover}
          onItemClick={handleLegendClick}
          getInvestmentHref={getBnUrl}
          isItemClickable={isClickable}
        />
      </Box>
    </Container>
  );
};
